// ** Modules
import React from 'react';
import createCache from '@emotion/cache';

// ** UI
import { useTheme } from '@mui/material/styles';

// ** Utils
import { getSubdomain } from '@/utils/domain/regions';

// ** Types
import type { AppContext } from '@/types/contextTypes';

export type UseBgColorType = {
	[key: string]: {
		color: string;
		backgroundColor: string;
	};
};

export const ColorModeContext = React.createContext({
	toggleColorMode: () => {
		return;
	}
});

export const createEmotionCache = () => {
	return createCache({ key: 'css' });
};

/**
 * Hex color to RGBA color
 * Handles 3, 6, and 8-digit hex codes
 */
export const hexToRGBA = (hexCode: string, opacity?: number) => {
	let hex = hexCode.replace('#', '');

	if (hex.length === 3) {
		hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
	}

	const r = parseInt(hex.substring(0, 2), 16);
	const g = parseInt(hex.substring(2, 4), 16);
	const b = parseInt(hex.substring(4, 6), 16);

	let a = opacity !== undefined ? opacity : 1;

	if (hex.length === 8) {
		a = parseInt(hex.substring(6, 8), 16) / 255;
	}

	return `rgba(${r}, ${g}, ${b}, ${a})`;
};

export const useBgColor = () => {
	const theme = useTheme();

	const getColorSet = (colorName: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info') => ({
		[`${colorName}Filled`]: {
			color: theme.palette[colorName].contrastText,
			backgroundColor: theme.palette[colorName].main
		},
		[`${colorName}Light`]: {
			color: theme.palette[colorName].main,
			backgroundColor: hexToRGBA(theme.palette[colorName].main, 0.12)
		}
	});

	return {
		...getColorSet('primary'),
		...getColorSet('secondary'),
		...getColorSet('success'),
		...getColorSet('error'),
		...getColorSet('warning'),
		...getColorSet('info')
	};
};

// Domain-specific Logos
const logos: Record<string, string> = {
	nrw: '/images/logos/tischlerNrw.svg',
	be: '/images/logos/tischlerBerlin.svg',
	hh: '/images/logos/tischlerNord.svg',
	'nord-hh': '/images/logos/tischlerNord.svg',
	ni: '/images/logos/tischlerNord.svg',
	www: '/images/logos/tischlerNrw.svg',
	bund: '/images/logos/TSD-Deutschland.svg',
	bb: '/images/logos/tischlerBrandenburg.svg',
	bw: '/images/logos/tischlerBadenWuerttemberg.svg',
	he: '/images/logos/tischlerHessen.svg',
	mv: '/images/logos/tischlerMecklenburgVorpommern.svg',
	th: '/images/logos/tischlerThueringen.svg',
	rp: '/images/logos/tischlerRheinlandPfalz.svg',
	sn: '/images/logos/tischlerSachsen.svg',
	st: '/images/logos/tischlerSachsenAnhalt.svg',
	dus: '/images/logos/tischlerInnungDuesseldorf.svg',
	beta: '/images/logos/tischlerBeta.svg',
	bie: '/images/logos/tischlerInnungBielefeld.svg',
	ruhr: '/images/logos/tischlerInnungRuhr.svg',
	kassel: '/images/logos/tischlerInnungKassel.svg',
	hsk: '/images/logos/tischlerInnungHochsauerland.svg',
	eus: '/images/logos/tischlerInnungEuskirchen.svg',
	rheydt: '/images/logos/tischlerInnungMoenchengladbachRheydt.svg',
	mkr: '/images/logos/tischlerInnungMaerkischeRegion.svg',
	koeln: '/images/logos/tischlerInnungKoeln.svg',
	default: '/images/logos/TSD-Wuerfel.svg',
	live: '/images/logos/tischlerNrw.svg' // Live Beta
};

export const logoSwitcher = (host: AppContext['host']) => {
	const domain = getSubdomain(host);

	const logo = logos[domain];
	// Check if the logo exists in the logos object
	return logo ? logo : '/images/placeholders/image200.png';
};
